import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { LegendListRef, LegendListMetrics } from "@legendapp/list/react";
import {
  captureThreadScrollAnchor,
  resolveThreadScrollAnchor,
  readThreadScrollPosition,
  saveThreadScrollPosition,
  type ThreadScrollPosition,
} from "./threadScrollPosition";

export function useThreadTimelineScroll({
  routeThreadKey,
  listRef,
  rows,
  onIsAtEndChange,
}: {
  readonly routeThreadKey: string;
  readonly listRef: RefObject<LegendListRef | null>;
  readonly rows: readonly { readonly kind: string; readonly id: string }[];
  readonly onIsAtEndChange: (isAtEnd: boolean) => void;
}) {
  const [initialPosition] = useState(() => readThreadScrollPosition(routeThreadKey));
  const headerSizeRef = useRef(0);
  const scrollGeneration = useRef(0);
  const readingPosition = useRef<ThreadScrollPosition | null>(initialPosition);
  const handleMetricsChange = (metrics: LegendListMetrics) => {
    headerSizeRef.current = metrics.headerSize;
  };
  const handleScroll = useCallback(() => {
    const state = listRef.current?.getState?.();
    if (state) {
      const position: ThreadScrollPosition = {
        offset: state.scroll,
        isAtEnd: state.isAtEnd,
        anchor:
          typeof state.positionAtIndex === "function"
            ? captureThreadScrollAnchor(
                rows,
                state.start,
                state.scroll,
                (index) => state.positionAtIndex(index) + headerSizeRef.current,
              )
            : null,
      };
      readingPosition.current = position;
      saveThreadScrollPosition(routeThreadKey, position);
      onIsAtEndChange(state.isAtEnd);
    }
  }, [listRef, onIsAtEndChange, routeThreadKey, rows]);

  const anchor = resolveThreadScrollAnchor(rows, initialPosition);
  const restoredAnchorRef = useRef(false);
  const anchorIndex = anchor?.index ?? null;
  const anchorViewOffset = anchor?.viewOffset ?? 0;
  useEffect(() => {
    if (restoredAnchorRef.current || anchorIndex === null) return;
    const frame = window.requestAnimationFrame(() => {
      if (!listRef.current) return;
      restoredAnchorRef.current = true;
      void listRef.current.scrollToIndex({
        index: anchorIndex,
        viewOffset: anchorViewOffset,
        animated: false,
      });
      onIsAtEndChange(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [anchorIndex, anchorViewOffset, listRef, onIsAtEndChange]);

  const retainedRowCount = useRef(rows.length);
  useEffect(() => {
    const changed = retainedRowCount.current !== rows.length;
    retainedRowCount.current = rows.length;
    if (!changed) return;
    const readingAnchor = resolveThreadScrollAnchor(rows, readingPosition.current);
    if (!readingAnchor) return;
    const generation = scrollGeneration.current;
    const frame = window.requestAnimationFrame(() => {
      if (generation !== scrollGeneration.current) return;
      void listRef.current?.scrollToIndex({ ...readingAnchor, animated: false });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [rows, listRef]);

  const previousRowCountRef = useRef(0);
  useEffect(() => {
    const previousRowCount = previousRowCountRef.current;
    previousRowCountRef.current = rows.length;

    if (previousRowCount > 0 || rows.length === 0 || initialPosition?.isAtEnd === false) {
      return;
    }

    onIsAtEndChange(true);
    const frameId = window.requestAnimationFrame(() => {
      void listRef.current?.scrollToEnd?.({ animated: false });
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [initialPosition, listRef, onIsAtEndChange, rows.length]);

  return {
    initialPosition,
    cancelPendingRestore: () => {
      scrollGeneration.current += 1;
    },
    scrollHandlers: { onScroll: handleScroll, onMetricsChange: handleMetricsChange },
    anchor,
  };
}
